// src/features/inventory/pages/SupplierDetailPage/tabs/index.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  Inventory2Outlined as ItemsIcon,
} from '@mui/icons-material';
import Details from './Details';
import Items from './Items';
import { useInventoryItemPermissions } from '../../../../auth/hooks/usePermissions';

function TabPanel({ children, value, index, sx }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`supplier-tabpanel-${index}`}
      aria-labelledby={`supplier-tab-${index}`}
      sx={value !== index ? null : { ...sx, minHeight: 0 }}
    >
      {value === index && <Box sx={{ minHeight: 0, ...sx }}>{children}</Box>}
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `supplier-tab-${index}`,
    'aria-controls': `supplier-tabpanel-${index}`,
  };
}

export default function SupplierTabs({ supplier }) {
  const { t } = useTranslation(['inventory', 'common']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentTab, setCurrentTab] = useState(0);
  const { canRead: canReadItems } = useInventoryItemPermissions();

  const tabs = [
    {
      key: 'details',
      label: t('inventory:suppliers.detail.tabs.details.title'),
      description: t('inventory:suppliers.detail.tabs.details.description'),
      icon: InfoIcon,
      component: Details,
      props: { supplier },
    },
    ...(canReadItems ? [{
      key: 'items',
      label: t('inventory:suppliers.detail.tabs.items.title'),
      description: t('inventory:suppliers.detail.tabs.items.description'),
      icon: ItemsIcon,
      component: Items,
      props: { supplierId: supplier.supplier_id },
    }] : []),
  ];

  return (
    <Box sx={{ width: '100%', mt: 3, flex: 1, minHeight: 0 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          height: '100%',
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(_, v) => setCurrentTab(v)}
          aria-label="supplier detail tabs"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              minHeight: isMobile ? 56 : 64,
              py: isMobile ? 1.5 : 2,
              px: isMobile ? 1.5 : 3,
              '&.Mui-selected': { fontWeight: 600, bgcolor: 'background.paper' },
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.key}
                icon={<Icon sx={{ fontSize: isMobile ? 34 : 36 }} />}
                iconPosition="start"
                label={
                  <Box sx={{ textAlign: 'left', ml: isMobile ? 0.5 : 1, pb: 1 }}>
                    <Typography variant="body2" fontWeight="inherit">{tab.label}</Typography>
                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary">{tab.description}</Typography>
                    )}
                  </Box>
                }
                sx={{ alignItems: 'center', justifyContent: 'flex-start', minWidth: 'unset', height: '100%' }}
                {...a11yProps(index)}
              />
            );
          })}
        </Tabs>

        <Box sx={{ bgcolor: 'background.paper', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {tabs.map((tab, index) => {
            const TabComponent = tab.component;
            return (
              <TabPanel
                key={tab.key}
                value={currentTab}
                index={index}
                sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <TabComponent {...tab.props} />
              </TabPanel>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}
